import { PDFDocument, degrees } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { StudioPage } from '../types';
import { exportDocumentToPdf } from '../../../core/export/pdfExporter';
import { DocumentModel } from '../../../core/ast/document.types';

// Configure PDF.js worker dynamically in browser environment
if (typeof window !== 'undefined' && pdfjsLib.GlobalWorkerOptions) {
  try {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version || '4.10.38'}/pdf.worker.min.mjs`;
  } catch {
    // Ignore worker configuration failure in test or SSR environments
  }
}

/**
 * Renders a high-fidelity image data URL from a PDF page using PDF.js and Canvas
 */
export async function renderPdfPageToDataUrl(
  pdfBytes: Uint8Array,
  pageIndex: number,
  scale: number = 0.5
): Promise<string> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return generatePdfPageThumbnailPlaceholder(pageIndex + 1);
  }

  try {
    // Copy the byte buffer to prevent DetachedArrayBuffer errors
    const bufferCopy = pdfBytes.slice(0);
    const loadingTask = pdfjsLib.getDocument({ data: bufferCopy });
    const pdf = await loadingTask.promise;
    
    // PDF.js uses 1-indexed pages
    const targetPageIndex = Math.min(Math.max(1, pageIndex + 1), pdf.numPages);
    const page = await pdf.getPage(targetPageIndex);

    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const context = canvas.getContext('2d');

    if (!context) {
      return generatePdfPageThumbnailPlaceholder(pageIndex + 1);
    }

    // Fill white background before rendering
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };

    await page.render(renderContext).promise;
    return canvas.toDataURL('image/jpeg', 0.88);
  } catch (err) {
    console.warn('PDF.js render fallback:', err);
    return generatePdfPageThumbnailPlaceholder(pageIndex + 1);
  }
}

/**
 * Parses an uploaded PDF file into an array of StudioPage objects with real thumbnails
 */
export async function parsePdfToStudioPages(
  fileBytes: Uint8Array,
  fileName: string,
  sourceId: string
): Promise<StudioPage[]> {
  const pdfDoc = await PDFDocument.load(fileBytes);
  const pageCount = pdfDoc.getPageCount();
  const pages: StudioPage[] = [];

  for (let i = 0; i < pageCount; i++) {
    const pageId = `p-${Date.now().toString(36)}-${i}-${Math.random().toString(36).substring(2, 6)}`;
    const page = pdfDoc.getPage(i);
    const rotation = page.getRotation().angle || 0;

    let thumbnailDataUrl: string;
    try {
      thumbnailDataUrl = await renderPdfPageToDataUrl(fileBytes, i, 0.45);
    } catch {
      thumbnailDataUrl = generatePdfPageThumbnailPlaceholder(i + 1);
    }

    pages.push({
      id: pageId,
      sourceDocId: sourceId,
      sourceDocName: fileName,
      sourceType: 'imported_pdf',
      pageIndexInSource: i,
      rotation,
      thumbnailDataUrl,
      pdfBytes: fileBytes,
    });
  }

  return pages;
}

/**
 * Converts a structured DocumentModel into StudioPages for Page Studio manipulation
 */
export async function parseAstToStudioPages(
  docModel: DocumentModel
): Promise<StudioPage[]> {
  const pdfBytes = await exportDocumentToPdf(docModel);
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pageCount = pdfDoc.getPageCount();
  const sourceName = docModel.metadata.title || 'Generated Report';
  const sourceId = docModel.metadata.id;
  const pages: StudioPage[] = [];

  for (let i = 0; i < pageCount; i++) {
    const pageId = `p-${Date.now().toString(36)}-${i}-${Math.random().toString(36).substring(2, 6)}`;
    const page = pdfDoc.getPage(i);
    const rotation = page.getRotation().angle || 0;

    let thumbnailDataUrl: string;
    try {
      thumbnailDataUrl = await renderPdfPageToDataUrl(pdfBytes, i, 0.45);
    } catch {
      thumbnailDataUrl = generatePdfPageThumbnailPlaceholder(i + 1);
    }

    pages.push({
      id: pageId,
      sourceDocId: sourceId,
      sourceDocName: sourceName,
      sourceType: 'generated_ast',
      pageIndexInSource: i,
      rotation,
      thumbnailDataUrl,
      pdfBytes,
      astModel: docModel,
    });
  }

  return pages;
}

/**
 * Creates a clean blank A4 StudioPage
 */
export async function createBlankStudioPage(pageNumber: number = 1): Promise<StudioPage> {
  const blankDoc = await PDFDocument.create();
  blankDoc.addPage([595.28, 841.89]); // standard A4 in points
  const pdfBytes = await blankDoc.save();
  const pageId = `p-blank-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
  const sourceId = `blank-${Date.now().toString(36)}`;

  let thumbnailDataUrl = generatePdfPageThumbnailPlaceholder(pageNumber);
  try {
    thumbnailDataUrl = await renderPdfPageToDataUrl(pdfBytes, 0, 0.45);
  } catch {}

  return {
    id: pageId,
    sourceDocId: sourceId,
    sourceDocName: 'Blank Page',
    sourceType: 'imported_pdf',
    pageIndexInSource: 0,
    rotation: 0,
    thumbnailDataUrl,
    pdfBytes,
  };
}

/**
 * Assembles and compiles the final PDF from StudioPages in exact order with rotations applied
 */
export async function compileStudioPagesToPdf(studioPages: StudioPage[]): Promise<Uint8Array> {
  const outputDoc = await PDFDocument.create();
  const loadedPdfCache = new Map<string, PDFDocument>();

  for (const p of studioPages) {
    if (!p.pdfBytes) continue;

    let sourcePdf = loadedPdfCache.get(p.sourceDocId);
    if (!sourcePdf) {
      sourcePdf = await PDFDocument.load(p.pdfBytes);
      loadedPdfCache.set(p.sourceDocId, sourcePdf);
    }

    const totalSourcePages = sourcePdf.getPageCount();
    const targetIdx = Math.min(Math.max(0, p.pageIndexInSource), totalSourcePages - 1);

    const [copiedPage] = await outputDoc.copyPages(sourcePdf, [targetIdx]);
    
    if (p.rotation !== 0) {
      const currentRot = copiedPage.getRotation().angle;
      copiedPage.setRotation(degrees((currentRot + p.rotation) % 360));
    }

    outputDoc.addPage(copiedPage);
  }

  return await outputDoc.save();
}

/**
 * Extracts a subset of StudioPages and compiles them into a standalone PDF
 */
export async function extractStudioPagesToPdf(selectedPages: StudioPage[]): Promise<Uint8Array> {
  return await compileStudioPagesToPdf(selectedPages);
}

/**
 * Splits StudioPages by custom ranges
 */
export async function splitStudioPagesByRanges(
  allPages: StudioPage[],
  ranges: Array<{ from: number; to: number; label?: string }>
): Promise<Array<{ label: string; bytes: Uint8Array; pageCount: number }>> {
  const results: Array<{ label: string; bytes: Uint8Array; pageCount: number }> = [];

  for (const range of ranges) {
    const startIdx = Math.max(0, range.from - 1);
    const endIdx = Math.min(allPages.length - 1, range.to - 1);
    if (startIdx > endIdx) continue;

    const slice = allPages.slice(startIdx, endIdx + 1);
    if (slice.length === 0) continue;

    const bytes = await compileStudioPagesToPdf(slice);
    results.push({
      label: range.label || `Pages_${range.from}_to_${range.to}`,
      bytes,
      pageCount: slice.length,
    });
  }

  return results;
}

/**
 * Generates an SVG-based preview thumbnail for a page as a reliable fallback
 */
export function generatePdfPageThumbnailPlaceholder(pageNum: number): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="424" viewBox="0 0 300 424">
    <rect width="300" height="424" fill="#ffffff" stroke="#cbd5e1" stroke-width="2" rx="4"/>
    <rect x="25" y="25" width="120" height="8" rx="4" fill="#94a3b8"/>
    <line x1="25" y1="40" x2="275" y2="40" stroke="#e2e8f0" stroke-width="1.5"/>
    <rect x="25" y="60" width="180" height="12" rx="3" fill="#2563eb" opacity="0.8"/>
    <rect x="25" y="82" width="250" height="6" rx="2" fill="#e2e8f0"/>
    <rect x="25" y="94" width="235" height="6" rx="2" fill="#e2e8f0"/>
    <rect x="25" y="106" width="245" height="6" rx="2" fill="#e2e8f0"/>
    <rect x="25" y="118" width="190" height="6" rx="2" fill="#e2e8f0"/>
    <rect x="25" y="140" width="250" height="80" rx="4" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
    <rect x="40" y="160" width="140" height="8" rx="2" fill="#64748b"/>
    <rect x="40" y="176" width="200" height="6" rx="2" fill="#cbd5e1"/>
    <rect x="40" y="190" width="180" height="6" rx="2" fill="#cbd5e1"/>
    <rect x="25" y="235" width="250" height="90" rx="4" fill="#ffffff" stroke="#cbd5e1" stroke-width="1"/>
    <line x1="25" y1="260" x2="275" y2="260" stroke="#cbd5e1" stroke-width="1"/>
    <line x1="25" y1="285" x2="275" y2="285" stroke="#e2e8f0" stroke-width="1"/>
    <line x1="25" y1="310" x2="275" y2="310" stroke="#e2e8f0" stroke-width="1"/>
    <line x1="108" y1="235" x2="108" y2="325" stroke="#e2e8f0" stroke-width="1"/>
    <line x1="192" y1="235" x2="192" y2="325" stroke="#e2e8f0" stroke-width="1"/>
    <line x1="25" y1="385" x2="275" y2="385" stroke="#e2e8f0" stroke-width="1"/>
    <rect x="130" y="395" width="40" height="6" rx="2" fill="#94a3b8"/>
    <rect x="15" y="15" width="30" height="22" rx="4" fill="#1e293b"/>
    <text x="30" y="31" font-family="sans-serif" font-size="12" font-weight="bold" fill="#ffffff" text-anchor="middle">${pageNum}</text>
  </svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
